import { getCurrentScope, onScopeDispose, readonly, shallowRef, watch, type Ref } from 'vue'

/** Follows `source`, holding each value at least `minMs`; values arriving inside the hold are not queued, the latest shows. */
export function useSteadyValue<T>(source: () => T, minMs: number): Readonly<Ref<T>> {
  const value = shallowRef(source())
  let shownAt = Date.now()
  let timer: ReturnType<typeof setTimeout> | undefined

  function show(next: T): void {
    value.value = next
    shownAt = Date.now()
  }

  watch(source, (next) => {
    clearTimeout(timer)
    timer = undefined
    const wait = minMs - (Date.now() - shownAt)
    if (wait <= 0) {
      show(next)
      return
    }
    timer = setTimeout(() => {
      timer = undefined
      show(source())
    }, wait)
  })

  if (getCurrentScope()) onScopeDispose(() => clearTimeout(timer))
  return readonly(value) as Readonly<Ref<T>>
}
